import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    TextField,
    useTheme,
    Link
} from '@mui/material';
import '../../index.css';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSearchParams } from "react-router-dom";
import { useAuth } from '../../context/AuthContext';

const RESETPASS = "/resetpass/";

export const PassChange = () => {
    const theme = useTheme();
    const [linkClicked, setLinkClicked] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');      
    const [newPasswordError, setNewPasswordError] = useState('');   
    const [repeatPasswordError, setRepeatPasswordError] = useState('');
    const [searchParams] = useSearchParams();
    const resetCode = searchParams.get("code");
    const [showNewPassword, setShowNewPassword] = useState(true);
    const [showRepeatNewPassword, setShowRepeatNewPassword] = useState(true);
    const [isValidating, setIsValidating] = useState(true);    
    const navigate = useNavigate();    
    const auth = useAuth();

    useEffect(() => {
        const validateResetCode = async () => {
            if (!resetCode) {
                navigate('/login');
                return;
            }
            //Validate token
            try {
                const res = await axios.post(RESETPASS, { 
                    resetCode: resetCode,
                    checkTokenValidation: true
                });
                    console.log("response", res);
                if (res.data.code === 0 && res.data.isValid) {
                    setIsValidating(false);
                } else {                    
                    navigate('/resetpass', { 
                        state: { expiredLink: true }
                    });
                }
            } catch (error) {
                navigate('/resetpass', { 
                    state: { expiredLink: true }
                });
            }
        };

        validateResetCode();
    }, [resetCode, navigate]);

    //Change password and login 
    const sendPassChangeRequestRequest = async (resetCodeSent, newPass) => {
        try {
            setLinkClicked(true);
            const res = await axios.post(RESETPASS, {
                resetCode: resetCodeSent,
                password: newPass
            });

            const { code, message, userEmail } = res.data;

            if (message === "Your password has been updated successfully.") {
                try {
                    const response = await axios.post('/login', {
                        email: userEmail,
                        password: newPass,
                        deviceId: "web",
                    });

                    if (response.status === 200 && response.data?.code === 0) {
                        auth.login(
                            response.data.message.token.id,
                            response.data.message.user.id,
                            response.data.message.user.role,
                            `${response.data.message.user.first} ${response.data.message.user.last}`,
                            response.data.message.user.orgId
                        );
                        navigate("/plans");
                    } else {
                        console.log("Email and password didn’t match");
                    }
                } catch (error) {
                    console.error("Login error:", error);
                }
            }
        } catch (err) {
            console.error("Error archiving plan:", err);
        }
    };

    if (isValidating) {
        return null;
    }

    return (
        <>
            <Box sx={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#fff',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflowY: 'hidden'
            }} >

                <Box sx={{
                    width: '100%',
                    maxWidth: 600,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 2
                }} >
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/; 
                            
                            let errorFlag = false;

                            if ((newPassword || "") !== (repeatPassword || "")) {
                                setRepeatPasswordError(true);
                                errorFlag = true;
                            }
                            if (!passwordRegex.test(newPassword || "")) {
                                setNewPasswordError(true);
                                errorFlag = true;
                            }

                            if(!errorFlag){
                                sendPassChangeRequestRequest(resetCode, newPassword);
                            }
                        }}
                        style={{
                            width: '100%',
                            maxWidth: 600,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                        }}
                    >

                        {/* Logo */}
                        <Typography
                            variant="h2"
                            sx={{
                                textAlign: 'center',
                                width: '100%',
                                color: '#1565C0',
                            }}
                        >
                            <span style={{ color: '#1565C0' }}>Ocu</span>
                            <strong style={{ color: '#000000', fontWeight: 'bold' }}>Build</strong>
                        </Typography>

                        {/* Title */}
                        <Typography gutterBottom sx={{ fontSize: 38, mb: 0, mt: 1 }}>
                            Password Reset
                        </Typography>

                        <Typography variant="body2" gutterBottom sx={{ fontSize: '1.05em', mb: 0, mt: -2 }}>
                            Enter a new password to reset your password and sign into OcuBuild.
                        </Typography>

                        {/* Password Fields */}
                        <TextField
                            fullWidth
                            id="new-password-field"
                            label="New password"
                            type={showNewPassword ? "password" : "text"} 
                            variant="standard"
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                setNewPasswordError('');
                            }}
                            error={!!newPasswordError}
                            helperText={"Use at least 8 characters, with a letter and a number"}
                            autoComplete="off"                            
                            onCopy={(e) => e.preventDefault()}
                            onPaste={(e) => e.preventDefault()}
                            onCut={(e) => e.preventDefault()}
                            onDrag={(e) => e.preventDefault()}
                            onDrop={(e) => e.preventDefault()}
                            onSelect={(e) => e.preventDefault()}
                            sx={{
                                '& .MuiInputBase-root': {
                                    '&:-webkit-autofill': {
                                        WebkitBoxShadow: '0 0 0 100px #E8F0FE inset',
                                        WebkitTextFillColor: '#000',
                                    },
                                },
                            }}
                            FormHelperTextProps={{
                                sx: newPasswordError ? { color: "error.main" } : {},
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            edge="end"
                                        >
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            id="re-type-password-field"
                            label="Re-enter new password"
                            type={showRepeatNewPassword ? "password" : "text"}
                            variant="standard"
                            value={repeatPassword}
                            onChange={(e) => {
                            setRepeatPassword(e.target.value);
                            setRepeatPasswordError('');
                            // setLoginError(false);
                            }}
                            error={!!repeatPasswordError}
                            helperText={"Passwords must match"}
                            autoComplete="new-password"
                            onCopy={(e) => e.preventDefault()}
                            onPaste={(e) => e.preventDefault()}
                            onCut={(e) => e.preventDefault()}
                            onDrag={(e) => e.preventDefault()}
                            onDrop={(e) => e.preventDefault()}
                            sx={{
                                '& .MuiInputBase-root': {
                                    '&:-webkit-autofill': {
                                        WebkitBoxShadow: '0 0 0 100px #E8F0FE inset',
                                        WebkitTextFillColor: '#000',
                                    },
                                },
                            }}
                            FormHelperTextProps={{
                                sx: repeatPasswordError ? { color: "error.main" } : {},
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowRepeatNewPassword(!showRepeatNewPassword)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            edge="end"
                                        >
                                            {showRepeatNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Sign In + Forgot */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                mt: 4,
                            }}
                        >
                            <Button
                                variant="contained"
                                size="medium"
                                sx={{ height: 40 }}
                                type="submit"
                                disabled={linkClicked}
                            >
                                RESET PASSWORD
                            </Button>

                            <Link
                                variant="body2"
                                underline="none"
                                disabled={linkClicked}
                                onClick={() => navigate('/login')}
                                sx={{
                                    color: theme.palette.grey[600],
                                    ml: 2,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                Back to sign in
                            </Link>
                        </Box>
                    </form>
                </Box>
            </Box>
        </>
    );
};
